import { DirectionsBoat as BoatIcon } from '@mui/icons-material';
import AllTerrainVehicleIcon from 'Components/icons/AllTerrainVehicleIcon';
import SideBySideIcon from 'Components/icons/SideBySideIcon';
import SnowmobileIcon from 'Components/icons/SnowmobileIcon';
import ThreeWheelIcon from 'Components/icons/ThreeWheelIcon';
import WatercraftIcon from 'Components/icons/WatercraftIcon';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';

export const PRODUCT_LINE_DISPLAY_ORDER = [
  'ATVS',
  'SIDE_BY_SIDE',
  'WATERCRAFT',
  'SNOWMOBILE',
  'TWO_WHEEL',
  'THREE_WHEEL',
  'BOATS',
];

const normalizeProductLineKey = input => {
  if (!input) return '';
  if (typeof input === 'string') return input.toUpperCase();
  return (input.internalName || input.code || input.id || '').toUpperCase();
};

const getProductLineOrderValue = input => {
  const normalized = normalizeProductLineKey(input);
  const index = PRODUCT_LINE_DISPLAY_ORDER.indexOf(normalized);
  return index === -1 ? PRODUCT_LINE_DISPLAY_ORDER.length : index;
};

export const sortProductLinesByDisplayOrder = (productLines = []) => {
  return [...productLines].sort((a, b) => {
    const orderDelta = getProductLineOrderValue(a) - getProductLineOrderValue(b);
    if (orderDelta !== 0) {
      return orderDelta;
    }

    const aName = normalizeProductLineKey(a);
    const bName = normalizeProductLineKey(b);
    return aName.localeCompare(bName);
  });
};

/**
 * Get active product offerings for a dealer
 * @param {Object} dealer - Dealer object with offering flags
 * @param {Array} allProductLines - Array of all product lines
 * @returns {Array} Array of active product offerings
 */
export const getActiveProductOfferings = (dealer, allProductLines = []) => {
  if (!dealer) return [];

  const activeOfferings = allProductLines.filter(pl => pl.isActive);

  return sortProductLinesByDisplayOrder(
    activeOfferings.filter(offering => (dealer.dealerProductLines ?? []).some(line => line.id === offering.id)),
  );
};

/**
 * Get color for a specific product line by key
 * @param {string} key - Product offering key (e.g., 'offersSno', 'offersPwc')
 * @returns {string} Hex color code or default gray if not found
 */
export const getProductLineColor = key => {
  const colorMap = {
    // By key
    offersSno: '#00A6E8',
    offersPwc: '#0078D4',
    offersBt: '#006BA6',
    offersAtv: '#FF6B35',
    offersRds: '#D62828',
    offersSsv: '#F77F00',
    offersTwoWheel: '#FFB703',
    // By internalName
    SNOWMOBILE: '#00A6E8',
    WATERCRAFT: '#0078D4',
    BOATS: '#006BA6',
    ATVS: '#FF6B35',
    THREE_WHEEL: '#D62828',
    SIDE_BY_SIDE: '#F77F00',
    TWO_WHEEL: '#FFB703',
  };
  return colorMap[key] || '#6B7280';
};

/**
 * Get icon component for a specific product line by key or internalName
 * @param {string} keyOrInternalName - Product offering key (e.g., 'offersSno') or internalName (e.g., 'SNOWMOBILE')
 * @returns {React.Component} Icon component or default icon if not found
 */
export const getProductLineIcon = keyOrInternalName => {
  const iconMap = {
    // By key
    offersSno: SnowmobileIcon,
    offersPwc: WatercraftIcon,
    offersBt: BoatIcon,
    offersAtv: AllTerrainVehicleIcon,
    offersRds: ThreeWheelIcon,
    offersSsv: SideBySideIcon,
    offersTwoWheel: TwoWheelerIcon,
    // By internalName
    SNOWMOBILE: SnowmobileIcon,
    WATERCRAFT: WatercraftIcon,
    BOATS: BoatIcon,
    ATVS: AllTerrainVehicleIcon,
    THREE_WHEEL: ThreeWheelIcon,
    SIDE_BY_SIDE: SideBySideIcon,
    TWO_WHEEL: TwoWheelerIcon,
  };
  return iconMap[keyOrInternalName] || AllTerrainVehicleIcon;
};
